<template>
  <div class="status-loader" v-if="!isReady">
    <div class="preloader-wrapper active">
      <div class="spinner-layer spinner-teal-only">
        <div class="circle-clipper left">
          <div class="circle"></div>
        </div>
        <div class="gap-patch">
          <div class="circle"></div>
        </div>
        <div class="circle-clipper right">
          <div class="circle"></div>
        </div>
      </div>
    </div>
  </div>

  <form @submit.prevent="handleSubmit" v-else>
    <div class="card-panel status-confirmation">
      <div
        class="status-confirmation-icon"
        :class="{ danger: !actionIsRestore, success: actionIsRestore }"
      >
        <i class="material-icons">{{ actionIsRestore ? "restore" : "person_off" }}</i>
      </div>

      <h5>{{ actionTitle }}</h5>
      <p class="confirmation-copy grey-text text-darken-1">{{ actionCopy }}</p>

      <div class="status-summary">
        <div>
          <span>Principal</span>
          <strong>{{ enrollee.name }}</strong>
        </div>
        <div>
          <span>Current Status</span>
          <strong>{{ enrollee.status == "d" ? "Inactive" : "Active" }}</strong>
        </div>
        <div>
          <span>After Confirmation</span>
          <strong>{{ actionIsRestore ? "Active or Lobby" : "Inactive" }}</strong>
        </div>
      </div>

      <div class="deactivation-options" v-if="!actionIsRestore">
        <button
          type="button"
          class="deactivation-option"
          :class="{ active: deactivationMode == 'administrative' }"
          @click="deactivationMode = 'administrative'"
        >
          <i class="material-icons deactivation-choice-icon">
            {{ deactivationMode == "administrative" ? "check_circle" : "radio_button_unchecked" }}
          </i>
          <span class="deactivation-copy">
            <strong>Administrative deactivation</strong>
            <small>Set the principal and family inactive. Paid cover remains available until its natural end date.</small>
          </span>
        </button>
        <button
          type="button"
          class="deactivation-option"
          :class="{ active: deactivationMode == 'stop_access' }"
          @click="deactivationMode = 'stop_access'"
        >
          <i class="material-icons deactivation-choice-icon">
            {{ deactivationMode == "stop_access" ? "check_circle" : "radio_button_unchecked" }}
          </i>
          <span class="deactivation-copy">
            <strong>Deactivate and <b class="critical-phrase">stop access</b></strong>
            <small>Set the principal and family inactive, then close active enrollee access from yesterday.</small>
          </span>
        </button>
      </div>

      <p class="red-text text-darken-2" v-if="error">{{ error }}</p>

      <div class="status-actions">
        <a
          href="#"
          class="btn-flat waves-effect"
          :class="{ disabled: isSubmitting }"
          @click.prevent="handleBack"
        >
          Cancel
        </a>
        <button
          class="waves-effect waves-light btn-large"
          :class="{
            disabled: !isReady || isSubmitting || (!actionIsRestore && !deactivationMode),
            red: !actionIsRestore,
            teal: actionIsRestore,
          }"
          v-if="isReady"
        >
          <i class="material-icons right">check_circle</i>
          {{ isSubmitting ? "Updating" : actionLabel }}
        </button>
      </div>
    </div>
  </form>
</template>
        
  <script>
import { computed, ref } from "vue";
import putItem from "@/composables/putItem";

export default {
  name: "EnrolleeStatusEditOnboardComponent",
  props: ["cat", "enrollee"],
  emits: ["reset"],
  setup(props, context) {
    const isSubmitting = ref(false);
    const error = ref(null);
    const deactivationMode = ref(null);
    const actionIsRestore = computed(() => props.enrollee?.status == "d");
    const newStatus = computed(() => (actionIsRestore.value ? "a" : "d"));
    const actionLabel = computed(() => {
      const name = props.enrollee?.name || "principal";
      return actionIsRestore.value ? `Restore ${name}` : `De-activate ${name}`;
    });
    const actionTitle = computed(() => (actionIsRestore.value ? "Restore principal record?" : "De-activate principal record?"));
    const actionCopy = computed(() => {
      if (actionIsRestore.value) {
        return "If current paid cover exists, this family returns to Active and access is regenerated. If not, they return to Lobby for invoice generation.";
      }

      return "This principal will be marked inactive after confirmation.";
    });
    const isReady = computed(() => {
      return Boolean(
        props.enrollee?.id &&
        props.enrollee?.name &&
        props.enrollee?.status &&
        actionLabel.value &&
        actionTitle.value &&
        actionCopy.value &&
        newStatus.value
      );
    });

    // handle back
    const handleBack = () => {
      context.emit("reset", null);
    };

    //handle submit
    const handleSubmit = async () => {
      // Native form validation handles custom errors \\

      // start
      isSubmitting.value = true;
      error.value = null;

      // set updated status
      // round up
      let actionUrl;
      const newVal = {
        type: "p", // principal
        status: newStatus.value,
      };

      if (!actionIsRestore.value) {
        newVal.deactivation_mode = deactivationMode.value;
      }

      // kill for TPA straightaway
      if (props.cat == 'tpa') {
       actionUrl = "operator/client/enrollee/status/kill";
      }else{
        actionUrl = "operator/client/enrollee/status/update";
      }

      // post item
      const { res, err } = await putItem(
        actionUrl,
        props.enrollee.id,
        newVal
      );

      if (res.value) {
        // start
        let principal;
        // next
        principal = {
          ...props.enrollee,
          ...res.value.data,
        };

        // go back
        context.emit("reset", principal);
      } else {
        error.value = err.value;
      }

      // show button back
      isSubmitting.value = false;
    };

    return {
      actionCopy,
      actionIsRestore,
      actionLabel,
      actionTitle,
      deactivationMode,
      handleBack,
      handleSubmit,
      error,
      newStatus,
      isReady,
      isSubmitting,
    };
  },
};
</script>
        
<style scoped>
.status-loader {
  align-items: center;
  display: flex;
  justify-content: center;
  min-height: 260px;
}

.status-confirmation {
  border: 1px solid #eeeeee;
  border-radius: 8px;
  margin-bottom: 0;
  padding: 24px;
  box-shadow: none;
  text-align: center;
}

.status-confirmation h5 {
  color: #263238;
  font-weight: 600;
  margin: 12px 0 8px;
}

.confirmation-copy {
  margin-left: auto;
  margin-right: auto;
  max-width: 620px;
}

.status-confirmation-icon {
  align-items: center;
  border-radius: 50%;
  display: inline-flex;
  height: 58px;
  justify-content: center;
  width: 58px;
}

.status-confirmation-icon.danger {
  background: #ffebee;
  color: #c62828;
}

.status-confirmation-icon.success {
  background: #e0f2f1;
  color: #00796b;
}

.status-confirmation-icon i {
  font-size: 30px;
}

.status-summary {
  border: 1px solid #e5e5e5;
  border-radius: 8px;
  margin: 22px auto;
  max-width: 620px;
  overflow: hidden;
  text-align: left;
}

.status-summary div {
  align-items: flex-start;
  border-bottom: 1px solid #eeeeee;
  display: grid;
  gap: 18px;
  grid-template-columns: minmax(130px, 220px) 1fr;
  padding: 13px 16px;
}

.status-summary div:last-child {
  border-bottom: 0;
}

.status-summary span {
  color: #607d8b;
  font-size: 0.85rem;
  font-weight: 600;
}

.status-summary strong {
  color: #263238;
  font-weight: 500;
}

.status-actions {
  align-items: center;
  display: flex;
  gap: 10px;
  justify-content: center;
  margin-top: 24px;
}

.deactivation-options {
  display: grid;
  gap: 10px;
  margin: 18px auto 0;
  max-width: 620px;
  text-align: left;
}

.deactivation-option {
  align-items: flex-start;
  background: #ffffff;
  border: 1px solid #eeeeee;
  border-radius: 8px;
  color: inherit;
  cursor: pointer;
  display: flex;
  gap: 10px;
  padding: 14px;
  text-align: left;
  width: 100%;
}

.deactivation-option.active {
  background: #fff8e1;
  border-color: #ffcc80;
}

.deactivation-copy,
.deactivation-copy small,
.deactivation-copy strong {
  display: block;
}

.deactivation-copy small {
  color: #607d8b;
  line-height: 1.5;
  margin-top: 4px;
}

.critical-phrase {
  font-size: 1.12rem;
}

.deactivation-choice-icon {
  color: #f57c00;
  flex: 0 0 auto;
  font-size: 22px;
  margin-top: 1px;
}
</style>
